import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import {
  User, Building2, Mail, Phone, MapPin, Save, Shield, Key, Bell, Smartphone, Inbox, Palette
} from 'lucide-react';

const Settings = () => {
  const { user, refreshUser } = useAuth();
  const token = localStorage.getItem('nexus_token');
  const [profile, setProfile] = useState({ name: '', email: '', phone: '' });
  const [company, setCompany] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ current: '', newPass: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState({ in_app: true, push: true, email: true });
  const [savingPrefs, setSavingPrefs] = useState(false);
  // Master Directive #32 — per-user accent color
  const [accentColor, setAccentColor] = useState('#F5B301');
  const [savingAccent, setSavingAccent] = useState(false);

  const isAdmin = user?.role === 'department_admin' || user?.role === 'main_hub';

  useEffect(() => {
    if (user) {
      setProfile({ name: user.name || '', email: user.email || '', phone: user.phone || '' });
      if (user.accent_color) setAccentColor(user.accent_color);
    }
  }, [user]);

  const fetchCompany = useCallback(async () => {
    if (!user?.company_id) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/companies/${user.company_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setCompany(await res.json());
    } catch (e) { console.error(e); }
  }, [token, user?.company_id]);

  useEffect(() => { fetchCompany(); }, [fetchCompany]);

  // Load notification prefs
  useEffect(() => {
    if (!token) return;
    fetch(`${BACKEND_URL}/api/users/me/notification-prefs`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setNotifPrefs({
        in_app: data.in_app !== false,
        push: data.push !== false,
        email: data.email !== false,
      }); })
      .catch(() => {});
  }, [token]);

  const handleToggleNotif = async (key) => {
    const next = { ...notifPrefs, [key]: !notifPrefs[key] };
    setNotifPrefs(next);
    setSavingPrefs(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/users/me/notification-prefs`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [key]: next[key] }),
      });
      if (!res.ok) throw new Error('Failed');
      toast({ title: 'Saved', description: `${key.replace('_', ' ')} notifications ${next[key] ? 'enabled' : 'disabled'}` });
    } catch {
      setNotifPrefs(notifPrefs); // revert
      toast({ title: 'Error', description: 'Could not save preference', variant: 'destructive' });
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/users/${user.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(profile)
      });
      if (res.ok) {
        toast({ title: 'Success', description: 'Profile updated' });
        refreshUser();
      } else {
        const err = await res.json();
        throw new Error(err.detail || 'Update failed');
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.newPass || passwordForm.newPass !== passwordForm.confirm) {
      toast({ title: 'Error', description: 'Passwords do not match', variant: 'destructive' });
      return;
    }
    if (passwordForm.newPass.length < 6) {
      toast({ title: 'Error', description: 'Password must be at least 6 characters', variant: 'destructive' });
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/users/${user.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ password: passwordForm.newPass })
      });
      if (res.ok) {
        toast({ title: 'Success', description: 'Password changed' });
        setPasswordForm({ current: '', newPass: '', confirm: '' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  // Master Directive #32 — save accent color (server rejects white/black).
  const handleSaveAccent = async () => {
    setSavingAccent(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/users/${user.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ accent_color: accentColor }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      toast({ title: 'Saved', description: 'Accent color updated' });
      refreshUser();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSavingAccent(false);
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      main_hub: 'bg-red-500', department_admin: 'bg-amber-500', field_investigator: 'bg-blue-500',
      skip_tracer: 'bg-purple-500', transporter: 'bg-cyan-500', client: 'bg-green-500'
    };
    return <Badge className={`${colors[role] || 'bg-slate-500'} text-white`}>{role?.replace(/_/g, ' ')}</Badge>;
  };

  return (
    <div className="space-y-6 w-full max-w-full" data-testid="settings-page">
      <div>
        <div className="nx-eyebrow">Administration</div>
        <h1 className="nx-h1 mt-1">Settings</h1>
        <p className="nx-body mt-1">Manage your profile and preferences</p>
      </div>

      {/* Dashboard customization — Compact / Comfortable density + metric prioritization */}
      <DashboardPreferences />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile */}
        <Card className="bg-[#1B2D47] border-[#2A3F5F] text-white">
          <CardHeader>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <User className="w-4 h-4 text-amber-400" /> Profile Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #3B82F6, #1D4ED8)' }}>
                <span className="text-white font-bold text-xl">{user?.name?.charAt(0)?.toUpperCase()}</span>
              </div>
              <div>
                <h3 className="text-white font-semibold">{user?.name}</h3>
                <div className="flex items-center gap-2">
                  {getRoleBadge(user?.role)}
                  <span className="text-xs text-slate-400">@{user?.username}</span>
                </div>
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Full Name</Label>
              <Input value={profile.name} onChange={e => setProfile({ ...profile, name: e.target.value })}
                className="bg-[#0F1B2D] border-[#2A3F5F] text-white placeholder:text-slate-500" data-testid="settings-name-input" />
            </div>
            <div>
              <Label className="text-slate-300">Email</Label>
              <Input value={profile.email} onChange={e => setProfile({ ...profile, email: e.target.value })}
                className="bg-[#0F1B2D] border-[#2A3F5F] text-white placeholder:text-slate-500" data-testid="settings-email-input" />
            </div>
            <div>
              <Label className="text-slate-300">Phone</Label>
              <Input value={profile.phone} onChange={e => setProfile({ ...profile, phone: e.target.value })}
                className="bg-[#0F1B2D] border-[#2A3F5F] text-white placeholder:text-slate-500" />
            </div>
            <Button onClick={handleSaveProfile} disabled={saving} className="w-full bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="save-profile-btn">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Profile'}
            </Button>
          </CardContent>
        </Card>

        {/* Password */}
        <Card className="bg-[#1B2D47] border-[#2A3F5F] text-white">
          <CardHeader>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Key className="w-4 h-4 text-amber-400" /> Change Password
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-slate-300">New Password</Label>
              <Input type="password" value={passwordForm.newPass}
                onChange={e => setPasswordForm({ ...passwordForm, newPass: e.target.value })}
                className="bg-[#0F1B2D] border-[#2A3F5F] text-white placeholder:text-slate-500" placeholder="Enter new password" data-testid="new-password-input" />
            </div>
            <div>
              <Label className="text-slate-300">Confirm Password</Label>
              <Input type="password" value={passwordForm.confirm}
                onChange={e => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                className="bg-[#0F1B2D] border-[#2A3F5F] text-white placeholder:text-slate-500" placeholder="Confirm new password" />
            </div>
            <Button onClick={handleChangePassword} variant="outline" className="w-full border-slate-300 text-slate-300" data-testid="change-password-btn">
              <Shield className="w-4 h-4 mr-2" /> Change Password
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Master Directive #32 — Accent Color */}
      <Card className="bg-[#1B2D47] border-[#2A3F5F] text-white" data-testid="accent-color-card">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Palette className="w-4 h-4 text-amber-400" /> Personal Accent Color
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-300">
            Pick an accent color for your account UI highlights. This affects
            personal accent elements only — logos, banners, and brand colors
            are preserved.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={accentColor}
                onChange={e => setAccentColor(e.target.value)}
                className="w-14 h-14 rounded cursor-pointer border-2 border-[#2A3F5F] bg-[#0F1B2D]"
                data-testid="accent-color-picker"
                aria-label="Accent color"
              />
              <div className="flex flex-col gap-1">
                <Input
                  value={accentColor}
                  onChange={e => setAccentColor(e.target.value)}
                  placeholder="#F5B301"
                  className="bg-[#0F1B2D] border-[#2A3F5F] text-white placeholder:text-slate-500 w-32 font-mono uppercase text-sm"
                  data-testid="accent-color-hex"
                />
                <span className="text-[10px] text-slate-500 font-mono uppercase tracking-wider">Hex code</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {['#F5B301', '#DC2626', '#059669', '#2563EB', '#7C3AED', '#EC4899', '#0891B2'].map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setAccentColor(c)}
                  className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${accentColor.toLowerCase() === c.toLowerCase() ? 'border-white ring-2 ring-amber-300' : 'border-slate-600'}`}
                  style={{ backgroundColor: c }}
                  title={c}
                  data-testid={`accent-swatch-${c.slice(1)}`}
                  aria-label={`Set accent to ${c}`}
                />
              ))}
            </div>
          </div>
          <div
            className="rounded-md p-3 border border-slate-600 flex items-center gap-3"
            style={{ background: 'linear-gradient(135deg, #0F1B2D, #1B2D47)' }}
            data-testid="accent-preview"
          >
            <div className="w-10 h-10 rounded" style={{ backgroundColor: accentColor }} />
            <div>
              <p className="text-white text-sm font-heading">Preview</p>
              <p className="text-[10px] font-mono uppercase tracking-widest" style={{ color: accentColor }}>
                Accent applied to highlights, focus rings, chips
              </p>
            </div>
          </div>
          <p className="text-[10px] text-slate-500">
            White (#FFFFFF) and black (#000000) are not allowed as accents.
          </p>
          <Button
            onClick={handleSaveAccent}
            disabled={savingAccent}
            className="bg-[#0F172A] hover:bg-[#1E293B] text-white"
            data-testid="save-accent-btn"
          >
            <Save className="w-4 h-4 mr-2" /> {savingAccent ? 'Saving…' : 'Save Accent Color'}
          </Button>
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card className="bg-[#1B2D47] border-[#2A3F5F] text-white" data-testid="notification-prefs-card">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Bell className="w-4 h-4 text-amber-500" /> Notifications
          </CardTitle>
        </CardHeader>
        <CardContent>          <p className="text-xs text-slate-500 mb-4">
            Choose how NexusTrac reaches you for case assignments, updates, and field alerts.
            Security and password-reset notifications are always delivered and cannot be muted.
          </p>
          <div className="space-y-3">
            {[
              { key: 'in_app', icon: Inbox, label: 'In-app notifications', help: 'Shown inside the NexusTrac UI' },
              { key: 'push', icon: Smartphone, label: 'Web push notifications', help: 'Desktop alerts via OneSignal' },
              { key: 'email', icon: Mail, label: 'Email notifications', help: `Delivered to ${user?.email || 'your account email'}` },
            ].map(({ key, icon: Icon, label, help }) => (
              <div key={key} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 sm:px-4 py-3 w-full max-w-full box-border" data-testid={`notif-row-${key}`}>
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <Icon className="w-4 h-4 mt-0.5 text-slate-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800 truncate">{label}</div>
                    <div className="text-xs text-slate-500 break-words">{help}</div>
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={notifPrefs[key]}
                  disabled={savingPrefs}
                  onClick={() => handleToggleNotif(key)}
                  data-testid={`notif-toggle-${key}`}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-60 flex-shrink-0 ${notifPrefs[key] ? 'bg-emerald-500' : 'bg-slate-300'}`}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${notifPrefs[key] ? 'translate-x-5' : 'translate-x-1'}`} />
                </button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Company Info (Admin only) */}
      {isAdmin && company && (
        <Card className="bg-[#1B2D47] border-[#2A3F5F] text-white">
          <CardHeader>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Building2 className="w-4 h-4 text-amber-400" /> Company Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="w-4 h-4 text-slate-500" />
                  <span className="text-slate-400">Company:</span>
                  <span className="text-white">{company.name}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-slate-500" />
                  <span className="text-slate-400">Email:</span>
                  <span className="text-white">{company.contact_email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-slate-500" />
                  <span className="text-slate-400">Phone:</span>
                  <span className="text-white">{company.contact_phone || 'N/A'}</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-slate-500" />
                  <span className="text-slate-400">Address:</span>
                  <span className="text-white">{[company.address, company.city, company.state].filter(Boolean).join(', ')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-slate-400">Status:</span>
                  <Badge className="bg-green-500 text-white">{company.status}</Badge>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-slate-400">ID:</span>
                  <span className="text-slate-500 font-mono text-xs">{company.id}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Settings;

// ============================================================================
// DashboardPreferences — workspace customization (Compact vs Comfortable
// density + dashboard metric prioritization). Persisted via localStorage,
// read on Dashboard render via useDashboardPrefs hook.
// ============================================================================
const PREFS_STORAGE_KEY = 'nexus_dashboard_prefs';
const ALL_METRICS = [
  { key: 'total_cases', label: 'Total Cases' },
  { key: 'open_cases', label: 'Open Cases' },
  { key: 'closed_cases', label: 'Closed Cases' },
  { key: 'persons', label: 'Persons of Interest' },
  { key: 'vehicles', label: 'Vehicles Tracked' },
];

const loadDashboardPrefs = () => {
  try {
    const raw = localStorage.getItem(PREFS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const DashboardPreferences = () => {
  const stored = loadDashboardPrefs();
  const [density, setDensity] = useState(stored?.density || 'comfortable');
  const [enabledMetrics, setEnabledMetrics] = useState(stored?.metrics || ALL_METRICS.map(m => m.key));

  const save = (next) => {
    const merged = { density, metrics: enabledMetrics, ...next };
    setDensity(merged.density);
    setEnabledMetrics(merged.metrics);
    localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(merged));
    toast({ title: 'Workspace updated', description: 'Your dashboard layout has been saved.' });
  };

  const toggleMetric = (key) => {
    const next = enabledMetrics.includes(key)
      ? enabledMetrics.filter(k => k !== key)
      : [...enabledMetrics, key];
    save({ metrics: next });
  };

  return (
    <Card className="bg-white border-slate-200 w-full max-w-full" data-testid="dashboard-prefs-card">
      <CardHeader>
        <CardTitle className="text-slate-900 text-base flex items-center gap-2">
          <Smartphone className="w-4 h-4 text-amber-500" /> Workspace Customization
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Density */}
        <div>
          <div className="text-sm font-medium text-slate-800 mb-1">Layout density</div>
          <p className="text-xs text-slate-500 mb-2">Compact reduces spacing so more rows fit on screen. Comfortable adds breathing room.</p>
          <div className="grid grid-cols-2 gap-2 max-w-sm">
            {['compact', 'comfortable'].map(opt => (
              <button
                key={opt}
                type="button"
                onClick={() => save({ density: opt })}
                data-testid={`density-${opt}`}
                className={`px-4 py-2 rounded border text-sm font-medium capitalize transition-colors ${
                  density === opt
                    ? 'bg-red-600 border-red-500 text-white'
                    : 'bg-white border-slate-300 text-slate-700 hover:border-red-400'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        {/* Metric prioritization */}
        <div>
          <div className="text-sm font-medium text-slate-800 mb-1">Dashboard stat cards</div>
          <p className="text-xs text-slate-500 mb-2">Choose which top-row metrics appear on your dashboard.</p>
          <div className="space-y-2">
            {ALL_METRICS.map(m => {
              const on = enabledMetrics.includes(m.key);
              return (
                <div key={m.key} className="flex items-center justify-between gap-3 rounded border border-slate-200 px-3 py-2 w-full max-w-full box-border">
                  <span className="text-sm text-slate-800 truncate min-w-0 flex-1">{m.label}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={on}
                    onClick={() => toggleMetric(m.key)}
                    data-testid={`metric-toggle-${m.key}`}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${on ? 'bg-emerald-500' : 'bg-slate-300'}`}
                  >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${on ? 'translate-x-5' : 'translate-x-1'}`} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
